import React, { ReactNode, useRef, useState, useEffect } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { Heading } from '@abyss/web/ui/Heading';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import { CustomUseFormReturn } from '@abyss/web/hooks/useForm/v1/useForm';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { PageUpdatedSection } from '@src/components/global/PageUpdatedSection';
import { DetailWrapperController } from './DetailWrapper.Controller';
import { useBlocker } from '@src/components/global/NavigationBlocker/useBlocker';
import { ModalService } from '@src/api/common/ModalService';
import { SavePrompt } from '@src/components/global/SavePrompt';

type DetailWrapperProps = {
  form?: CustomUseFormReturn<any>;
  onSave?: () => Promise<any>;
  onDiscard?: () => Promise<any>;
  formData?: any;
  data: any;
  children: ReactNode;
  showUpdatedPageSection?: boolean;
};

export const DetailWrapper: React.FC<DetailWrapperProps> = ({
  data,
  children,
  form = null,
  onSave = undefined,
  onDiscard = undefined,
  formData = null,
  showUpdatedPageSection = true
}) => {
  const { isDeleted } = DetailWrapperController(data);
  const nextActionRef = useRef<any>(null);
  const { showModal, hideModal, modalState } = ModalService();

  const isFormDirty = form?.formState?.isDirty &&
    JSON.stringify(form?.formState?.dirtyFields) !== JSON.stringify({})
    || false;

  const Action = {
    UpdateModalState: async () => {
      modalState.title = 'Unsaved Changes';
      modalState.message =
        'You have unsaved changes. Do you want to leave this page without saving?';
      modalState.handleConfirm = Action.ConfirmNavigation;
      modalState.handleCancel = Action.CancelNavigation;
      modalState.cancelText = 'Stay';
      modalState.submitText = 'Leave';
      await showModal(modalState);
    },
    ConfirmNavigation: async () => {
      await hideModal();
      if (
        nextActionRef.current &&
        typeof nextActionRef.current.retry === 'function'
      ) {
        nextActionRef.current.retry();
      } else {
        console.error('Invalid nextAction:', nextActionRef.current);
      }
      nextActionRef.current = null;
    },
    CancelNavigation: async () => {
      await hideModal();
      nextActionRef.current = null;
    },
  };

  useBlocker((tx) => {
    nextActionRef.current = tx;
    showModal({
      key: 'unsaved-modal',
      title: 'Unsaved Changes',
      message:
        'You are about to leave this page with unsaved changes. Do you wish to continue?',
      handleConfirm: Action.ConfirmNavigation,
      handleCancel: Action.CancelNavigation,
      cancelText: 'Stay',
      submitText: 'Leave',
    });
  }, isFormDirty);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isFormDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty]);

  return (
    <React.Fragment>
      {isDeleted === null && <Skeleton height={350} width="100%" />}
      {isDeleted ? (
        <Box color="$white" align="center" padding="$xl">
          <Heading display="h5" color="$core.color.neutral.30">
            Record not found.
          </Heading>
        </Box>
      ) : (
        <React.Fragment>
          {form && onSave && formData ? (
            <FormProvider state={form} onSubmit={onSave}>
              <Box
                color="$primary2"
                css={{ 'abyss-box-root': { minHeight: '350px' } }}
              >
                {children}
                {showUpdatedPageSection ? (<PageUpdatedSection data={formData} />) : null}
              </Box>
              <SavePrompt discard={onDiscard} form={form} formData={formData} />
            </FormProvider>
          ) : (
            children
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
